// Screen: "Sign In / Sign Up"
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { lookupZipcode } from '../apiClient';
import SEO from '../components/SEO';

// Hardwood floor background style
const hardwoodFloorStyle = {
  background: `
    repeating-linear-gradient(90deg, #3d2817 0px, #4a3423 2px, #5c4033 4px, #4a3423 6px, #3d2817 8px),
    repeating-linear-gradient(0deg, transparent 0px, transparent 60px, rgba(0,0,0,0.15) 60px, rgba(0,0,0,0.15) 62px),
    linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
  `,
  backgroundBlendMode: "overlay, overlay, normal"
};

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

function InputField({ label, name, type = 'text', value, onChange, placeholder, required, mobile, scrollRef }) {
  const handleFocus = (e) => {
    // Scroll the input into view after iOS keyboard opens
    const el = e.target;
    setTimeout(() => {
      if (scrollRef?.current) {
        const container = scrollRef.current;
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop + (elRect.top - containerRect.top) - 50;
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      }
    }, 350);
  };

  return (
    <div style={{ marginBottom: mobile ? 14 : 20 }}>
      <label style={{
        display: 'block', fontSize: mobile ? 13 : 16, fontWeight: 500,
        marginBottom: mobile ? 5 : 8, color: '#c9d1d9'
      }}>
        {label}
      </label>
      <input
        type={type} name={name} value={value} onChange={onChange}
        placeholder={placeholder} required={required}
        autoComplete="off"
        onFocus={handleFocus}
        style={{
          width: '100%', padding: mobile ? '10px 12px' : '14px 16px', background: '#0d1117',
          border: '1px solid #30363d', borderRadius: 6, color: '#e2e8f0',
          fontSize: mobile ? 16 : 17, outline: 'none', boxSizing: 'border-box'
        }}
      />
    </div>
  );
}

export default function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signin, signup, token, updateUser } = useAuth();
  const isMobile = useIsMobile();
  const scrollRef = useRef(null);
  const referralCode = searchParams.get('ref') || '';
  const [authMode, setAuthMode] = useState(referralCode ? 'signup' : 'signin');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePassData, setChangePassData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changePassError, setChangePassError] = useState('');
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', firstName: '', lastName: '', nickname: '', zipCode: ''
  });
  const [nicknameEdited, setNicknameEdited] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zipPlaces, setZipPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [zipLooking, setZipLooking] = useState(false);

  const doZipLookup = useCallback(async (zip) => {
    if (!/^\d{5}$/.test(zip)) { setZipPlaces([]); setSelectedPlace(null); return; }
    setZipLooking(true);
    const data = await lookupZipcode(zip);
    setZipPlaces(data.places || []);
    if (data.places && data.places.length === 1) {
      setSelectedPlace(data.places[0]);
    } else {
      setSelectedPlace(null);
    }
    setZipLooking(false);
  }, []);

  useEffect(() => {
    if (authMode === 'signup' && formData.zipCode.length === 5) {
      doZipLookup(formData.zipCode);
    } else {
      setZipPlaces([]);
      setSelectedPlace(null);
    }
  }, [formData.zipCode, authMode, doZipLookup]);

  // Auto-populate screen name — use "STATE FirstName" if zip resolved, otherwise just FirstName
  useEffect(() => {
    if (authMode === 'signup' && !nicknameEdited && formData.firstName) {
      const prefix = selectedPlace?.state ? `${selectedPlace.state} ` : '';
      setFormData(prev => ({ ...prev, nickname: `${prefix}${formData.firstName}` }));
    }
  }, [formData.firstName, selectedPlace, authMode, nicknameEdited]);

  const handleInputChange = (e) => {
    if (e.target.name === 'nickname') setNicknameEdited(true);
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      let result;
      if (authMode === 'admin') {
        result = await signin('admin', formData.password);
        if (result.success) {
          navigate('/home');
        }
      } else if (authMode === 'signin') {
        result = await signin(formData.email, formData.password);
        if (result.success && result.user?.tempPassword) {
          setShowChangePassword(true);
        } else if (result.success) {
          navigate('/home');
        }
      } else {
        // Zip/postal code is optional — international users may not have a US zip
        if (!formData.password || formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setIsSubmitting(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setIsSubmitting(false);
          return;
        }
        result = await signup(
          formData.email, formData.password, formData.firstName,
          formData.lastName, formData.nickname, formData.zipCode || '',
          selectedPlace?.city || null, selectedPlace?.state || null, referralCode || undefined
        );
        if (result.success) {
          setSignupSuccess(true);
          setTimeout(() => navigate('/home'), 3000);
        }
      }

      if (!result.success) {
        setError(result.error || 'Authentication failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePassError('');
    if (changePassData.newPassword !== changePassData.confirmPassword) {
      setChangePassError('Passwords do not match');
      return;
    }
    if (changePassData.newPassword.length < 6) {
      setChangePassError('Password must be at least 6 characters');
      return;
    }
    try {
      const { changePassword } = await import('../apiClient');
      const result = await changePassword(token, changePassData.currentPassword, changePassData.newPassword);
      if (result.success) {
        setShowChangePassword(false);
        updateUser({ tempPassword: false });
        navigate('/home');
      } else {
        setChangePassError(result.error || 'Failed to change password');
      }
    } catch (err) {
      setChangePassError(err.message);
    }
  };

  return (
    <>
    <SEO
      title="Sign In or Create Account"
      description="Sign in or create a free account to play Buck Euchre online. Track your stats, compete on leaderboards, and earn Bucks."
      path="/signin"
    />
    <div style={{ ...hardwoodFloorStyle, height: '100vh', overflow: 'hidden', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div
        ref={scrollRef}
        style={{
          height: '100%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: isMobile ? '12px' : 32,
          paddingBottom: isMobile ? 700 : 32,
        }}
      >
        <div style={{
          background: '#161b22', borderRadius: isMobile ? 12 : 16,
          padding: isMobile ? '20px 16px' : '48px 56px',
          width: '100%', maxWidth: 560, border: '1px solid #30363d',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          margin: isMobile ? '0 auto' : '40px auto'
        }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 16 : 36 }}>
            <img
              src="/BuckEuchreLogoWhite.png"
              alt="Euchre Table"
              style={{
                width: '100%', maxWidth: isMobile ? 200 : 400,
                height: 'auto', marginBottom: isMobile ? 4 : 16
              }}
            />
            {authMode === 'signup' && (
              <p style={{ color: '#8b949e', marginTop: 4, fontSize: isMobile ? 14 : 16 }}>Create your account</p>
            )}
            {authMode === 'signup' && referralCode && (
              <div style={{
                marginTop: 8, padding: '8px 14px', background: '#1c3d2a',
                border: '1px solid #3fb950', borderRadius: 6, color: '#3fb950',
                fontSize: isMobile ? 12 : 13, fontWeight: 600
              }}>
                You were referred! You'll both earn 50 Bucks when you sign up.
              </div>
            )}
            {authMode === 'admin' && (
              <p style={{ color: '#f59e0b', marginTop: 4, fontSize: isMobile ? 14 : 16 }}>Admin Login</p>
            )}
          </div>

          {error && (
            <div style={{
              background: '#3d1f1f', border: '1px solid #f85149', color: '#f85149',
              padding: isMobile ? '10px 14px' : '14px 18px', borderRadius: 6,
              marginBottom: isMobile ? 16 : 24, fontSize: isMobile ? 13 : 15
            }}>
              {error}
            </div>
          )}


          <form onSubmit={handleSubmit} autoComplete="off">
            {authMode === 'signup' && (
              <>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr',
                  gap: isMobile ? 10 : 14, marginBottom: isMobile ? 0 : 4
                }}>
                  <InputField label="First Name" name="firstName" value={formData.firstName} onChange={handleInputChange} required mobile={isMobile} scrollRef={scrollRef} />
                  <InputField label="Last Name" name="lastName" value={formData.lastName} onChange={handleInputChange} required mobile={isMobile} scrollRef={scrollRef} />
                </div>

                {/* Zip Code + City/State lookup */}
                <InputField label="Zip / Postal Code (optional)" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="US zip or postal code (optional)" mobile={isMobile} scrollRef={scrollRef} />

                {zipLooking && (
                  <p style={{ color: '#8b949e', fontSize: 13, margin: '-6px 0 12px' }}>Looking up zip code...</p>
                )}

                {zipPlaces.length > 1 && !selectedPlace && (
                  <div style={{ marginBottom: isMobile ? 14 : 20 }}>
                    <label style={{
                      display: 'block', fontSize: isMobile ? 13 : 16,
                      fontWeight: 500, marginBottom: 6, color: '#c9d1d9'
                    }}>
                      Select your city
                    </label>
                    {zipPlaces.map((p, i) => (
                      <button key={i} type="button" onClick={() => setSelectedPlace(p)} style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: isMobile ? '8px 12px' : '10px 16px',
                        background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
                        color: '#e2e8f0', fontSize: isMobile ? 14 : 15,
                        cursor: 'pointer', marginBottom: 6
                      }}>
                        {p.city}, {p.state}
                      </button>
                    ))}
                  </div>
                )}

                {selectedPlace && (
                  <div style={{
                    marginBottom: isMobile ? 14 : 20, padding: isMobile ? '8px 12px' : '10px 16px',
                    background: '#1c3d2a', border: '1px solid #3fb950', borderRadius: 6,
                    color: '#3fb950', fontSize: isMobile ? 14 : 15,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                  }}>
                    <span>{selectedPlace.city}, {selectedPlace.state}</span>
                    {zipPlaces.length > 1 && (
                      <button type="button" onClick={() => setSelectedPlace(null)} style={{
                        background: 'none', border: 'none', color: '#8b949e',
                        cursor: 'pointer', fontSize: 12
                      }}>Change</button>
                    )}
                  </div>
                )}

                {formData.zipCode.length === 5 && !zipLooking && zipPlaces.length === 0 && (
                  <p style={{
                    color: '#f85149', fontSize: 13, margin: '-6px 0 12px'
                  }}>Zip code not found. Please check and try again.</p>
                )}

                <InputField label="Screen Name" name="nickname" value={formData.nickname} onChange={handleInputChange} placeholder="Auto-filled from your state + name" required mobile={isMobile} scrollRef={scrollRef} />
              </>
            )}

            {authMode !== 'admin' && (
              <InputField label="Email" name="email" type="text" value={formData.email} onChange={handleInputChange} required mobile={isMobile} scrollRef={scrollRef} />
            )}
            {authMode === 'signup' && (
              <>
                <InputField label="Password" name="password" type="password" value={formData.password} onChange={handleInputChange} placeholder="At least 6 characters" required mobile={isMobile} scrollRef={scrollRef} />
                <InputField label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleInputChange} required mobile={isMobile} scrollRef={scrollRef} />
              </>
            )}
            {(authMode === 'signin' || authMode === 'admin') && (
              <InputField label="Password" name="password" type="password" value={formData.password} onChange={handleInputChange} required mobile={isMobile} scrollRef={scrollRef} />
            )}

            <button type="submit" disabled={isSubmitting} style={{
              width: '100%', background: '#238636', color: '#fff', border: 'none',
              padding: isMobile ? '14px 20px' : '14px 24px', borderRadius: 6,
              fontSize: isMobile ? 18 : 18, fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1, marginTop: 8
            }}>
              {isSubmitting ? 'Please wait...' : (authMode === 'admin' ? 'Admin Login' : authMode === 'signin' ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          {signupSuccess && (
            <div style={{
              textAlign: 'center', marginTop: isMobile ? 14 : 20,
              padding: isMobile ? '12px' : '16px',
              background: '#1c3d2a', borderRadius: 6, color: '#3fb950',
              fontSize: isMobile ? 14 : 15, fontWeight: 600
            }}>
              Account created! Check your email to verify and receive your 100 Welcome Bucks.
            </div>
          )}

          {showChangePassword && (
            <div style={{
              marginTop: isMobile ? 14 : 20, padding: isMobile ? 14 : 20,
              background: '#1a1f2e', borderRadius: 8, border: '1px solid #f0883e'
            }}>
              <h3 style={{ color: '#f0883e', fontSize: isMobile ? 14 : 16, marginBottom: isMobile ? 10 : 14 }}>Change Your Temporary Password</h3>
              {changePassError && (
                <div style={{ color: '#f85149', fontSize: isMobile ? 13 : 14, marginBottom: 10 }}>{changePassError}</div>
              )}
              <form onSubmit={handleChangePassword}>
                <InputField label="Current Password (from email)" name="currentPassword" type="password" value={changePassData.currentPassword} onChange={(e) => setChangePassData({ ...changePassData, currentPassword: e.target.value })} required mobile={isMobile} scrollRef={scrollRef} />
                <InputField label="New Password" name="newPassword" type="password" value={changePassData.newPassword} onChange={(e) => setChangePassData({ ...changePassData, newPassword: e.target.value })} required mobile={isMobile} scrollRef={scrollRef} />
                <InputField label="Confirm New Password" name="confirmPassword" type="password" value={changePassData.confirmPassword} onChange={(e) => setChangePassData({ ...changePassData, confirmPassword: e.target.value })} required mobile={isMobile} scrollRef={scrollRef} />
                <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                  <button type="submit" style={{
                    flex: 1, background: '#238636', color: '#fff', border: 'none',
                    padding: '12px', borderRadius: 6, fontSize: isMobile ? 14 : 15,
                    fontWeight: 600, cursor: 'pointer'
                  }}>Change Password</button>
                  <button type="button" onClick={() => { setShowChangePassword(false); navigate('/home'); }} style={{
                    background: '#4b5563', color: '#fff', border: 'none',
                    padding: '12px 16px', borderRadius: 6, fontSize: isMobile ? 14 : 15, cursor: 'pointer'
                  }}>Skip</button>
                </div>
              </form>
            </div>
          )}

          <div style={{
            textAlign: 'center', marginTop: isMobile ? 18 : 28,
            color: '#8b949e', fontSize: isMobile ? 14 : 15
          }}>
            {authMode === 'signin' ? (
              <>
                Don't have an account?{' '}
                <button onClick={() => { setAuthMode('signup'); setError(''); setSignupSuccess(false); }}
                  style={{ background: 'none', border: 'none', color: '#58a6ff', cursor: 'pointer', fontSize: isMobile ? 14 : 15 }}>
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button onClick={() => { setAuthMode('signin'); setError(''); setSignupSuccess(false); }}
                  style={{ background: 'none', border: 'none', color: '#58a6ff', cursor: 'pointer', fontSize: isMobile ? 14 : 15 }}>
                  Sign in
                </button>
              </>
            )}
          </div>
          {authMode === 'signup' && !signupSuccess && (
            <p style={{
              textAlign: 'center', marginTop: isMobile ? 14 : 24,
              padding: isMobile ? '10px' : '14px',
              background: '#1c3d2a', borderRadius: 6, color: '#3fb950',
              fontSize: isMobile ? 13 : 14
            }}>
              Verify your email to receive 100 Bucks!
            </p>
          )}

          <button onClick={() => navigate('/play?mode=free')} style={{
            display: 'block', width: '100%', marginTop: isMobile ? 14 : 24,
            background: 'transparent', border: '1px solid #6366f1', color: '#a78bfa',
            borderRadius: 6, padding: isMobile ? '10px 16px' : '12px 20px',
            fontSize: isMobile ? 14 : 15, fontWeight: 600, cursor: 'pointer', textAlign: 'center'
          }}>
            Play Free (No Account Needed)
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
